/**
 * The clinic's price list, as shown on Our Services and edited there by its admin.
 *
 * Four levels, the shape the page has always rendered:
 *
 *   direction ("For Expecting Mothers")
 *     -> section ("Individual Sessions" | "Subscriptions")
 *       -> type ("Prenatal Massage")
 *         -> option ("30 min", 120 RON)
 *
 * This is the clinic's only list of what it sells: the booking wizard and the purchase
 * dialog both read `IService` (./service), which is one of these options flattened
 * together with the type above it.
 *
 * An option carries its duration twice, for two readers. `durationRo`/`durationEn` are
 * what the page prints -- "5 x 30 min", "regiune", "20 min (membru superior)" -- while
 * `durationMinutes` and `sessionCount` are the numbers a calendar slot and a session
 * count are computed from. Neither can be derived from the other, which is why both are
 * asked for.
 *
 * Every label carries both languages. Romanian is what the clinic must write; English is
 * optional and falls back to Romanian, so a clinic that never translates still reads
 * correctly under either flag.
 */

import { localised, newKey } from './localisedText';

/**
 * What the API returns. Ids are stable: a save updates the nodes it recognises rather
 * than replacing the catalogue, because a therapist's disciplines point at a type and a
 * purchase records the option it was sold from.
 */
export interface IServiceCatalog {
    directions: IServiceDirection[];
}

export interface IServiceDirection {
    id: number;
    titleRo: string;
    titleEn?: string | null;
    sections: IServiceSection[];
}

export interface IServiceSection {
    id: number;
    titleRo: string;
    titleEn?: string | null;
    types: IServiceType[];
}

export interface IServiceType {
    id: number;
    nameRo: string;
    nameEn?: string | null;
    options: IServiceOption[];
}

export interface IServiceOption {
    id: number;
    durationRo: string;
    durationEn?: string | null;
    /** One session's length. What a calendar slot is sized from. */
    durationMinutes: number;
    /** More than one makes this a package: sold at the desk, never booked into a slot. */
    sessionCount: number;
    /** RON. */
    price: number;
    /**
     * The single this package is a block of. Null on a single, and on a package no clinic
     * has linked yet — which behaves as it did before the link existed.
     *
     * It is what makes a package and its sessions the same *discipline* to the API: who may
     * give it, and which plans can settle an appointment, both resolve through this. The
     * editor is where it is set, as the plan/session toggle on a price-list line.
     */
    basedOnOptionId?: number | null;
    /**
     * The discipline this line qualifies under, resolved by the API through
     * `basedOnOptionId`: a plan reports the type of the session it is a block of, and a
     * session -- or a plan nobody has linked -- reports its own type.
     *
     * Always sent, and never sent back: the writable end of the link is `basedOnKey` on the
     * draft. Read it rather than following `basedOnOptionId` yourself -- the same resolution
     * decides who may give a service and which plans settle an appointment, and it is worth
     * exactly one implementation, which is the API's.
     */
    disciplineTypeId: number;
}

/**
 * The draft the editor holds.
 *
 * Every node carries two identities. `id` is the server's, absent for a row added in this
 * session, and it is what tells the save to update rather than insert. `key` is generated
 * here and exists because `id` cannot do this job: React needs a stable identity for a row
 * the user is typing into, and a brand new row has no server id to offer.
 *
 * Numbers are strings, not numbers, because that is what a controlled number input holds.
 * An empty box has to stay empty while it is being retyped rather than snapping to 0.
 */
export interface CatalogDraft {
    directions: DirectionDraft[];
}

export interface DirectionDraft {
    key: string;
    /** The server's id, or undefined for a node added in this session. */
    id?: number;
    titleRo: string;
    titleEn: string;
    sections: SectionDraft[];
}

export interface SectionDraft {
    key: string;
    /** The server's id, or undefined for a node added in this session. */
    id?: number;
    titleRo: string;
    titleEn: string;
    types: TypeDraft[];
}

export interface TypeDraft {
    key: string;
    /** The server's id, or undefined for a node added in this session. */
    id?: number;
    nameRo: string;
    nameEn: string;
    options: OptionDraft[];
}

export interface OptionDraft {
    key: string;
    /** The server's id, or undefined for a node added in this session. */
    id?: number;
    durationRo: string;
    durationEn: string;
    durationMinutes: string;
    sessionCount: string;
    price: string;
    /**
     * The `key` of the session line this plan sells a block of. Empty on a session.
     *
     * A key rather than an id, because the session may have been added in the same unsaved
     * edit and have no id yet — and that is exactly the case the admin hits when setting up
     * a discipline for the first time. The request carries keys and the server resolves
     * them once every row exists; ids come back on the next load.
     */
    basedOnKey: string;
}

/**
 * What PUT /service-catalog takes: the same tree, trimmed, with the editor's `key`
 * dropped and the server's `id` kept. A node without an id is one to create.
 */
export interface ServiceCatalogRequest {
    directions: {
        id?: number;
        titleRo: string;
        titleEn: string;
        sections: {
            id?: number;
            titleRo: string;
            titleEn: string;
            types: {
                id?: number;
                nameRo: string;
                nameEn: string;
                options: {
                    id?: number;
                    /** This line's identity within this request, so a plan can point at it. */
                    key: string;
                    durationRo: string;
                    durationEn: string;
                    durationMinutes: number;
                    sessionCount: number;
                    price: number;
                    /** The `key` of the session this plan is a block of. Absent on a session. */
                    basedOnKey?: string;
                }[];
            }[];
        }[];
    }[];
}

// Re-exported rather than merely imported: `localised` was defined here first and is
// imported from here by the booking steps, the purchase dialog and Our Services. It now
// belongs to ./localisedText, which the About page shares, and this keeps those call
// sites pointing where they always have.
export { localised };

export const emptyOption = (): OptionDraft => ({
    key: newKey(),
    durationRo: '',
    durationEn: '',
    // An hour, once. The commonest line on any of these price lists, and a starting point
    // an admin overtypes rather than an empty box they have to work out how to fill.
    durationMinutes: '60',
    sessionCount: '1',
    price: '',
    // A session until somebody says otherwise, which is what the count above already says.
    basedOnKey: '',
});

/** New nodes arrive with one child, so an admin never faces an empty container to fill. */
export const emptyType = (): TypeDraft => ({
    key: newKey(),
    nameRo: '',
    nameEn: '',
    options: [emptyOption()],
});

export const emptySection = (): SectionDraft => ({
    key: newKey(),
    titleRo: '',
    titleEn: '',
    types: [emptyType()],
});

export const emptyDirection = (): DirectionDraft => ({
    key: newKey(),
    titleRo: '',
    titleEn: '',
    sections: [emptySection()],
});

/**
 * Opens the editor on what is currently published.
 *
 * Two passes rather than one, because a plan's link arrives as the *id* of the session it
 * is a block of while the draft speaks in keys — and the key of that session is only minted
 * as the tree is built. So the first pass builds every row and remembers which key each id
 * became; the second turns the ids into keys. Nothing else in the draft needs a second look.
 */
export const toCatalogDraft = (catalog: IServiceCatalog | undefined): CatalogDraft => {
    // Filled as the tree is built: which key each option id became, and which id each
    // plan is waiting to have turned into a key.
    const keyById = new Map<number, string>();
    const linkedIdByKey = new Map<string, number>();

    const directions: DirectionDraft[] = (catalog?.directions ?? []).map(direction => ({
        key: newKey(),
        id: direction.id,
        titleRo: direction.titleRo,
        titleEn: direction.titleEn ?? '',
        sections: direction.sections.map(section => ({
            key: newKey(),
            id: section.id,
            titleRo: section.titleRo,
            titleEn: section.titleEn ?? '',
            types: section.types.map(type => ({
                key: newKey(),
                id: type.id,
                nameRo: type.nameRo,
                nameEn: type.nameEn ?? '',
                options: type.options.map(option => {
                    // Derived from the id rather than minted, and only for options.
                    //
                    // A key is React's row identity, and a random one was fine while it
                    // stayed in the browser. It is now part of the request -- it is how a
                    // plan names its session -- and the page decides whether anything was
                    // edited by comparing two requests built from the same catalogue. Two
                    // fresh keys would never match, so every untouched draft would read as
                    // dirty and every Cancel would ask to discard nothing.
                    const key: string = optionKey(option.id);

                    keyById.set(option.id, key);

                    if (option.basedOnOptionId != null) {
                        linkedIdByKey.set(key, option.basedOnOptionId);
                    }

                    return {
                        key,
                        id: option.id,
                        durationRo: option.durationRo,
                        durationEn: option.durationEn ?? '',
                        durationMinutes: String(option.durationMinutes),
                        sessionCount: String(option.sessionCount),
                        // toString rather than toFixed: 120 reads as "120", not "120.00".
                        price: String(option.price),
                        // Filled by the pass below, once every key exists.
                        basedOnKey: '',
                    };
                }),
            })),
        })),
    }));

    forEachOptionDraft(directions, option => {
        const linkedId: number | undefined = linkedIdByKey.get(option.key);

        // A link to a line outside the tree cannot come from the API, which answers with
        // the whole catalogue -- but an empty key is the honest reading if it ever did,
        // and it is what an unlinked plan carries anyway.
        option.basedOnKey = linkedId != null ? keyById.get(linkedId) ?? '' : '';
    });

    return { directions };
};

/**
 * The draft key of a saved option: stable across loads, and distinct from the random keys
 * `emptyOption` mints for rows that have no id to derive one from.
 */
const optionKey = (id: number): string => `option-${id}`;

/** One walk of the draft's leaves, shared by everything below that needs all the options. */
export const forEachOptionDraft = (
    directions: DirectionDraft[],
    action: (option: OptionDraft) => void
): void => {
    directions.forEach(direction =>
        direction.sections.forEach(section =>
            section.types.forEach(type => type.options.forEach(action))));
};

export const toServiceCatalogRequest = (draft: CatalogDraft): ServiceCatalogRequest => ({
    directions: draft.directions.map(direction => ({
        id: direction.id,
        titleRo: direction.titleRo.trim(),
        titleEn: direction.titleEn.trim(),
        sections: direction.sections.map(section => ({
            id: section.id,
            titleRo: section.titleRo.trim(),
            titleEn: section.titleEn.trim(),
            types: section.types.map(type => ({
                id: type.id,
                nameRo: type.nameRo.trim(),
                nameEn: type.nameEn.trim(),
                options: type.options.map(option => ({
                    id: option.id,
                    // Sent for every line, since any of them may be what a plan points at
                    // -- including one added a moment ago that has no id to point at.
                    key: option.key,
                    durationRo: option.durationRo.trim(),
                    durationEn: option.durationEn.trim(),
                    durationMinutes: Number(option.durationMinutes),
                    sessionCount: Number(option.sessionCount),
                    price: Number(option.price),
                    // Only while the line still claims to be a plan. Typing the count
                    // back down to one makes it a session again, and a session that
                    // still named a block of something is what the API refuses.
                    ...(Number(option.sessionCount) > 1 && option.basedOnKey
                        ? { basedOnKey: option.basedOnKey }
                        : {}),
                })),
            })),
        })),
    })),
});

/**
 * The keys of every node the API would reject, so the editor can mark the field before
 * the round trip. Deliberately the same rules the server enforces -- required Romanian
 * text, a price that is a number and not negative -- rather than a stricter set that
 * would refuse something the API would have accepted.
 *
 * Returns keys, not messages: which field is wrong is enough for the editor to decide
 * what to highlight, and the wording belongs to the translation file.
 */
export const invalidNodeKeys = (draft: CatalogDraft): Set<string> => {
    const invalid: Set<string> = new Set<string>();

    // Which lines a plan is allowed to point at: the session-kind ones, and only those
    // still in the tree. Gathered once rather than searched per option.
    const sessionKeys: Set<string> = new Set<string>();

    forEachOptionDraft(draft.directions, option => {
        if (Number(option.sessionCount) === 1) {
            sessionKeys.add(option.key);
        }
    });

    draft.directions.forEach(direction => {
        if (direction.titleRo.trim().length === 0) {
            invalid.add(direction.key);
        }

        direction.sections.forEach(section => {
            if (section.titleRo.trim().length === 0) {
                invalid.add(section.key);
            }

            section.types.forEach(type => {
                if (type.nameRo.trim().length === 0) {
                    invalid.add(type.key);
                }

                type.options.forEach(option => {
                    const price: number = Number(option.price);
                    const isPriceUsable: boolean =
                        option.price.trim().length > 0 && Number.isFinite(price) && price >= 0;

                    if (option.durationRo.trim().length === 0
                        || !isPriceUsable
                        || !isCountable(option.durationMinutes)
                        || !isCountable(option.sessionCount)
                        || !isLinkUsable(option, sessionKeys)) {
                        invalid.add(option.key);
                    }
                });
            });
        });
    });

    return invalid;
};

/** One session line a plan can be a block of, as its picker needs it. */
export interface SessionChoice {
    key: string;
    /** The discipline and the line, since "45 min" alone names nothing. */
    label: string;
}

/**
 * Every session line in the whole catalogue, for the picker on a plan.
 *
 * The whole draft rather than the type the plan sits under: a clinic keeps its plans in an
 * *Abonamente* section of their own, so the session a plan is a block of is almost never a
 * sibling. Read from the draft rather than fetched, which is what lets a session added a
 * moment ago be picked before either has been saved.
 */
export const sessionChoices = (draft: CatalogDraft): SessionChoice[] => {
    const choices: SessionChoice[] = [];

    draft.directions.forEach(direction =>
        direction.sections.forEach(section =>
            section.types.forEach(type =>
                type.options.forEach(option => {
                    if (Number(option.sessionCount) !== 1) {
                        return;
                    }

                    choices.push({
                        key: option.key,
                        label: `${type.nameRo.trim() || '—'} · ${option.durationRo.trim() || '—'}`,
                    });
                }))));

    return choices;
};

/**
 * Whether a line's link points at something that is still there.
 *
 * **An unlinked plan is not an error.** The column is nullable and the API accepts a plan
 * that names nothing -- which is exactly the state the backfill leaves any plan it could
 * not match, and the state every plan is in until an admin opens this editor. Refusing it
 * here would be the stricter-than-the-server rule this whole function is documented not to
 * be, and it would block Save on a catalogue the clinic never touched.
 *
 * What is refused is a link that resolves to nothing: the session it named has been deleted
 * in this same edit. The server refuses that too, by name, so this is the same rule said
 * early enough to be fixed beside the field.
 *
 * A session carrying a link left over from a toggle is not asked about either --
 * `toServiceCatalogRequest` sends the link only while the line still sells more than one
 * session, so what a flipped toggle leaves behind never reaches the API.
 *
 * Exported for the editor, which asks the same question to decide what to redden.
 */
export const isLinkUsable = (option: OptionDraft, sessionKeys: Set<string>): boolean =>
    option.basedOnKey.length === 0 || sessionKeys.has(option.basedOnKey);

/**
 * The rule behind both of the numeric option fields, which the API states as @Min(1) on a
 * required Integer. Half a session and a three-and-a-half-minute appointment are not
 * things, so a fractional entry is rejected here rather than silently truncated on the
 * way to the server.
 *
 * Exported so the editor can mark the individual box at fault: `invalidNodeKeys` flags the
 * option row as a whole, which is the right grain for deciding whether Save is available
 * and the wrong one for deciding which field to redden.
 */
export const isCountable = (value: string): boolean => {
    const parsed: number = Number(value);

    return value.trim().length > 0 && Number.isInteger(parsed) && parsed >= 1;
};

/** One discipline a therapist can be assigned, as the admin picker needs it. */
export interface CatalogTypeChoice {
    id: number;
    name: string;
    /**
     * The direction it sits under, so two entries with the same name can be told apart.
     *
     * The direction alone, not the section: now that a discipline is one entry rather than
     * one per price-list row, the section beside it said "Ședințe individuale" on nearly
     * every line and distinguished nothing.
     */
    context: string;
}

/**
 * The disciplines a therapist can be assigned, in the order the price list reads.
 *
 * One entry per discipline rather than one per type, which is not the same thing. A clinic
 * that sells the same work both ways separates the two at the *section* level -- "Ședințe
 * individuale" and "Abonamente" -- so "Kinetoterapie" is two types with nothing between them
 * but a matching name. Listing both asked the admin to tick the same discipline twice, and
 * ticking one of the two silently half-assigned the therapist: the plan resolves to the
 * session's discipline (`disciplineTypeId`), so it is the session's tickbox that decides who
 * is offered for the subscription.
 *
 * So the list is every id some option *resolves to*, which is exactly the set
 * `findByCatalogType` can ever be asked about -- no more, and no less:
 *
 *   * a linked plan contributes the session's type, so the discipline appears once and
 *     ticking it covers every subscription built on that service;
 *   * an **unlinked** plan resolves to its own type and so stays listed. Nothing is ever
 *     unassignable, and it drops out of the list by itself once the admin links it on Our
 *     Services;
 *   * a type with no priced lines contributes nothing. It sells nothing, so there is nothing
 *     to be qualified for.
 *
 * Ordered by where the discipline itself sits in the catalogue rather than by where the
 * option that named it does, so the list still reads in price-list order however a clinic has
 * arranged its subscriptions.
 */
export const catalogTypeChoices = (
    catalog: IServiceCatalog | undefined,
    language: string
): CatalogTypeChoice[] => {
    const assignable: Set<number> = assignableTypeIds(catalog);

    return (catalog?.directions ?? []).flatMap(direction =>
        direction.sections.flatMap(section =>
            section.types
                .filter(type => assignable.has(type.id))
                .map(type => ({
                    id: type.id,
                    name: localised(type.nameRo, type.nameEn, language),
                    context: localised(direction.titleRo, direction.titleEn, language),
                }))
        )
    );
};

/**
 * The ids {@link catalogTypeChoices} renders, without needing a language to name them.
 *
 * Its own function because the picker asks this question twice and only one of the two is
 * about display: the stored mapping has to be narrowed to what the list can show before it
 * seeds the form, and doing that from the rendered choices would re-seed -- discarding
 * whatever the admin had ticked -- every time the flag changed. Ids do not depend on the
 * language, so this does not either.
 */
export const assignableTypeIds = (catalog: IServiceCatalog | undefined): Set<number> => {
    const typeIds: Set<number> = new Set<number>();
    const resolved: Set<number> = new Set<number>();

    (catalog?.directions ?? []).forEach(direction =>
        direction.sections.forEach(section =>
            section.types.forEach(type => {
                typeIds.add(type.id);
                type.options.forEach(option => resolved.add(option.disciplineTypeId));
            })));

    // Intersected rather than returned as gathered, so this really is the set the list
    // renders. A resolved id always names a type in the same tree, and skipping the check
    // would make that a claim about the API rather than a reading of what it sent.
    return new Set<number>(Array.from(resolved).filter(id => typeIds.has(id)));
};
